// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Ship Upgrades Configuration
 * 
 * Contains all ship upgrade definitions, costs, and availability.
 */

export const upgradesConfig = {
  // Shield upgrades
  shields: [
    {
      id: 'shield_1',
      name: 'Basic Shield Generator',
      tier: 1,
      cost: 500,
      shieldCapacity: 50, // Max shield points
      rechargeRate: 2, // Points per second
      description: 'Standard deflector shields. Absorbs damage before hull takes hits.',
      icon: '🛡️'
    },
    {
      id: 'shield_2',
      name: 'Advanced Shield Array',
      tier: 2,
      cost: 1200,
      shieldCapacity: 100,
      rechargeRate: 3,
      description: 'Enhanced shield matrix with faster recharge and higher capacity.',
      icon: '🛡️',
      requires: 'shield_1'
    },
    {
      id: 'shield_3',
      name: 'Military-Grade Shields',
      tier: 3,
      cost: 2500,
      shieldCapacity: 150,
      rechargeRate: 5,
      description: 'Top-tier military shields. Maximum protection and rapid recharge.',
      icon: '🛡️',
      requires: 'shield_2'
    }
  ],
  
  // Hull upgrades
  hull: [
    {
      id: 'hull_1',
      name: 'Reinforced Hull Plating',
      tier: 1,
      cost: 800,
      hullBonus: 25, // +25 max hull
      description: 'Titanium-alloy reinforcement increases hull integrity.',
      icon: '🔩'
    },
    {
      id: 'hull_2',
      name: 'Composite Armor',
      tier: 2,
      cost: 1500,
      hullBonus: 50,
      damageReduction: 0.1, // 10% damage reduction
      description: 'Advanced composite materials provide superior protection.',
      icon: '🔩',
      requires: 'hull_1'
    },
    {
      id: 'hull_3',
      name: 'Ablative Armor',
      tier: 3,
      cost: 3000,
      hullBonus: 100,
      damageReduction: 0.2, // 20% damage reduction
      description: 'Cutting-edge ablative plating dissipates incoming damage.',
      icon: '🔩',
      requires: 'hull_2'
    }
  ],
  
  // Engine upgrades
  engines: [
    {
      id: 'engine_1',
      name: 'Improved Warp Coils',
      tier: 1,
      cost: 600,
      warpSpeedBonus: 0.15, // 15% faster warp
      description: 'Enhanced warp coils reduce travel time between systems.',
      icon: '🚀'
    },
    {
      id: 'engine_2',
      name: 'Quantum Drive',
      tier: 2,
      cost: 1400,
      warpSpeedBonus: 0.30, // 30% faster warp
      fuelEfficiency: 0.1, // 10% less fuel consumption
      description: 'Quantum-enhanced engines for faster, more efficient travel.',
      icon: '🚀',
      requires: 'engine_1'
    },
    {
      id: 'engine_3',
      name: 'Hyperdrive System',
      tier: 3,
      cost: 2800,
      warpSpeedBonus: 0.50, // 50% faster warp
      fuelEfficiency: 0.2, // 20% less fuel consumption
      description: 'Experimental hyperdrive technology. Maximum speed and efficiency.',
      icon: '🚀',
      requires: 'engine_2'
    }
  ],
  
  // Sensor upgrades
  sensors: [
    {
      id: 'sensor_1',
      name: 'Enhanced Scanners',
      tier: 1,
      cost: 400,
      scanBonus: 0.25, // 25% more credits from scans
      description: 'Improved sensor arrays detect more valuable resources.',
      icon: '📡'
    },
    {
      id: 'sensor_2',
      name: 'Deep Space Scanner',
      tier: 2,
      cost: 900,
      scanBonus: 0.50, // 50% more credits from scans
      threatDetection: true, // Early warning for combat
      description: 'Advanced scanners with threat detection capabilities.',
      icon: '📡',
      requires: 'sensor_1'
    },
    {
      id: 'sensor_3',
      name: 'Quantum Sensor Array',
      tier: 3,
      cost: 1800,
      scanBonus: 1.0, // 100% more credits from scans
      threatDetection: true,
      autoScan: true, // Passive credit generation
      description: 'Military-grade sensors. Automatic resource detection.',
      icon: '📡',
      requires: 'sensor_2'
    }
  ],
  
  // Special upgrades
  special: [
    {
      id: 'fuel_tank_1',
      name: 'Expanded Fuel Tanks',
      tier: 1,
      cost: 800,
      fuelCapacityBonus: 25, // +25% fuel capacity
      description: 'Expand fuel storage capacity by 25%. Allows longer journeys without refueling.',
      icon: '⛽'
    },
    {
      id: 'fuel_tank_2',
      name: 'Deep Space Fuel Cells',
      tier: 2,
      cost: 1600,
      fuelCapacityBonus: 50, // +50% fuel capacity
      description: 'Advanced fuel cell technology doubles your range. Essential for frontier exploration.',
      icon: '⛽',
      requires: 'fuel_tank_1'
    },
    {
      id: 'auto_repair',
      name: 'Nano-Repair System',
      tier: 2,
      cost: 2000,
      repairRate: 1, // 1 hull point per 10 seconds
      description: 'Nanobots slowly repair hull damage over time.',
      icon: '🔧'
    },
    {
      id: 'emergency_repair',
      name: 'Emergency Repair Drones',
      tier: 3,
      cost: 3500,
      repairRate: 3, // 3 hull points per 10 seconds
      emergencyRepair: true, // Can repair critical systems
      description: 'Advanced repair drones for rapid hull restoration. Can repair critical damage.',
      icon: '🔧',
      requires: 'auto_repair'
    },
    {
      id: 'power_core_1',
      name: 'Enhanced Power Core',
      tier: 2,
      cost: 1800,
      powerBonus: 0.15, // 15% more power generation
      description: 'Upgraded reactor core provides 15% more power to all systems.',
      icon: '⚡'
    },
    {
      id: 'power_core_2',
      name: 'Fusion Reactor',
      tier: 3,
      cost: 3200,
      powerBonus: 0.30, // 30% more power generation
      description: 'Compact fusion reactor. Maximum power output for all ship systems.',
      icon: '⚡',
      requires: 'power_core_1'
    },
    {
      id: 'targeting_computer',
      name: 'Advanced Targeting Computer',
      tier: 2,
      cost: 1500,
      accuracyBonus: 0.15, // 15% better accuracy
      description: 'AI-assisted targeting increases weapon accuracy by 15%.',
      icon: '🎯'
    },
    {
      id: 'combat_ai',
      name: 'Combat AI System',
      tier: 3,
      cost: 2800,
      accuracyBonus: 0.25, // 25% better accuracy
      criticalBonus: 0.05, // +5% critical hit chance
      description: 'Military-grade combat AI. Superior accuracy and increased critical hits.',
      icon: '🎯',
      requires: 'targeting_computer'
    },
    {
      id: 'stealth_coating',
      name: 'Stealth Hull Coating',
      tier: 2,
      cost: 2200,
      combatAvoidance: 0.25, // 25% chance to avoid combat
      description: 'Radar-absorbing coating reduces detection. 25% chance to avoid hostile encounters.',
      icon: '👁️'
    },
    {
      id: 'cargo_expansion',
      name: 'Cargo Bay Expansion',
      tier: 1,
      cost: 1000,
      cargoBonus: 50, // +50% cargo capacity (future feature)
      description: 'Expanded cargo holds for future trading operations.',
      icon: '📦',
      comingSoon: true
    },
    {
      id: 'cloaking',
      name: 'Cloaking Device',
      tier: 3,
      cost: 5000,
      combatAvoidance: 0.5, // 50% chance to avoid combat
      description: 'Experimental cloaking technology. Avoid hostile encounters.',
      icon: '👁️',
      comingSoon: true
    },
    {
      id: 'warp_stabilizer',
      name: 'Warp Field Stabilizer',
      tier: 2,
      cost: 1700,
      warpSafety: 0.2, // 20% less warp event risk
      description: 'Stabilizes warp field geometry. Reduces dangerous warp event probability.',
      icon: '🌀'
    },
    {
      id: 'gravity_compensator',
      name: 'Inertial Dampeners',
      tier: 2,
      cost: 1900,
      maneuverability: 0.2, // 20% better maneuverability
      description: 'Gravity compensation system improves ship handling and combat maneuvers.',
      icon: '🔄'
    },
    {
      id: 'life_support_upgrade',
      name: 'Advanced Life Support',
      tier: 1,
      cost: 1200,
      crewEfficiency: 0.1, // 10% efficiency bonus (future feature)
      description: 'Enhanced environmental systems. Improves crew performance.',
      icon: '💨',
      comingSoon: true
    }
  ],
  
  // Upgrade availability by location type
  availability: {
    planet: ['shield_1', 'hull_1', 'engine_1', 'sensor_1', 'fuel_tank_1'], // Tier 1 only
    station: 'all' // All upgrades available at any station
  }
};
